// SPDX-License-Identifier: MIT
// Left-column SESSION HISTORY accordion (desktop). A read-only, 3-level expand/
// collapse view of a project's build history — sessions → requests → detail. It
// inhabits the existing "carnet d'artisan" aesthetic (2px ink borders, hard offset
// shadows, paper-warm, sage/orange dots) — it does NOT invent a new look. It REUSES
// the agent-panel step-row rendering by re-parsing each request's raw NDJSON via
// parseClaudeStreamLine into the SAME .agent-step / .agent-prose / .agent-outcome
// rows, rendered statically (no timer, no streaming state). Pure DOM builder: no
// Tauri, no global side effects, fully unit-testable. Web never mounts it.
//
// Toggle pattern mirrors project-preview-card's setProjOpen/setPlanOpen: a real
// <button> header carrying aria-expanded + a sibling body toggled via .hidden.
import { escapeHtml } from '../lib/escape';
import { parseClaudeStreamLine } from '../platform/claude-stream';
import { renderStaticFeed } from './agent-panel';
import type { Session, AgentRequest } from '../platform/session-policy';

function fmtSessionDate(iso: string): string {
  if (!iso) return 'session importée';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'session importée';
  // Compact local date + time, language-neutral enough for the carnet rail.
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 16).replace('T', ' ');
  }
}

function fmtTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(d);
  } catch {
    return d.toISOString().slice(11, 16);
  }
}

function promptSummary(prompt: string): string {
  const flat = prompt.replace(/\s+/g, ' ').trim();
  if (flat.length <= 80) return flat || '(sans intitulé)';
  return `${flat.slice(0, 80)}…`;
}

// LEVEL 3 — request detail: re-parse the raw NDJSON into the same friendly step
// rows the live agent panel shows, rendered read-only. Empty / all-noise → a
// gentle empty line.
// Prepends a "Ta demande" block showing the USER's full prompt BEFORE the agent
// activity, so the detail reads as a conversation: demand → response.
function renderRequestDetail(req: AgentRequest): HTMLElement {
  const detail = document.createElement('div');
  detail.className = 'session-history__detail';
  detail.setAttribute('data-component', 'request-detail');

  // ── User demand block — always prepended, full prompt (not truncated). ──
  // Uses innerHTML + escapeHtml (not textContent) so we can wrap the label in
  // a <span>. Long prompts wrap via overflow-wrap: anywhere in CSS → no overflow.
  const demand = document.createElement('div');
  demand.className = 'session-history__demand';
  demand.setAttribute('data-component', 'request-demand');
  demand.innerHTML = `<span class="session-history__demand-label">Ta demande</span><span class="session-history__demand-text">${escapeHtml(req.prompt)}</span>`;
  detail.appendChild(demand);

  const events = req.lines
    .map((line) => parseClaudeStreamLine(line))
    .filter((ev): ev is NonNullable<typeof ev> => ev !== null);

  if (events.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'session-history__empty';
    empty.textContent = 'aucune activité enregistrée';
    detail.appendChild(empty);
    return detail;
  }
  detail.appendChild(renderStaticFeed(events));
  return detail;
}

// LEVEL 2 — one request row: clickable header (summary + badge + time) → detail.
function renderRequestRow(req: AgentRequest): HTMLElement {
  const row = document.createElement('div');
  row.className = 'session-history__request';
  row.setAttribute('data-component', 'request-row');
  row.dataset.requestId = req.id;

  const isBuild = req.kind === 'build';
  const badgeLabel = isBuild ? 'build' : 'relance';
  const time = fmtTime(req.startedAt);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'session-history__request-toggle';
  toggle.setAttribute('data-component', 'request-toggle');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.innerHTML = `
    <span class="session-history__caret" aria-hidden="true"></span>
    <span class="session-history__request-prompt">${escapeHtml(promptSummary(req.prompt))}</span>
    <span class="session-history__badge session-history__badge--${isBuild ? 'build' : 'followup'}" data-component="request-badge">${escapeHtml(badgeLabel)}</span>
    ${time ? `<span class="session-history__time">${escapeHtml(time)}</span>` : ''}`;

  // LAZY detail: an empty container until first expand. Eagerly building
  // renderRequestDetail (which parses ALL of req.lines) for every request froze the
  // UI on open / on every refresh for a large feed (2.5.8 regression). Parse only when
  // the user actually opens this request.
  const detail = document.createElement('div');
  detail.className = 'session-history__detail';
  detail.setAttribute('data-component', 'request-detail');
  detail.hidden = true;
  let built = false;

  const setOpen = (open: boolean): void => {
    if (open && !built) {
      const full = renderRequestDetail(req); // builds the demand block + parsed feed
      while (full.firstChild) detail.appendChild(full.firstChild); // move content in (no nesting)
      built = true;
    }
    detail.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('is-open', open);
  };
  toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));

  row.appendChild(toggle);
  row.appendChild(detail);
  return row;
}

// LEVEL 1 — one session row: clickable header (date + request count) → its requests.
function renderSessionRow(session: Session): HTMLElement {
  const row = document.createElement('div');
  row.className = 'session-history__session';
  row.setAttribute('data-component', 'session-row');
  row.dataset.sessionId = session.id;

  const count = session.requests.length;
  const countLabel = count === 1 ? '1 requête' : `${count} requêtes`;

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'session-history__session-toggle';
  toggle.setAttribute('data-component', 'session-toggle');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.innerHTML = `
    <span class="session-history__caret" aria-hidden="true"></span>
    <span class="session-history__session-date">${escapeHtml(fmtSessionDate(session.startedAt))}</span>
    <span class="session-history__count">${escapeHtml(countLabel)}</span>`;

  const body = document.createElement('div');
  body.className = 'session-history__session-body';
  body.setAttribute('data-component', 'session-body');
  body.hidden = true;
  for (const req of session.requests) body.appendChild(renderRequestRow(req));

  const setOpen = (open: boolean): void => {
    body.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('is-open', open);
  };
  toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));

  row.appendChild(toggle);
  row.appendChild(body);
  return row;
}

// Public builder. `projectKey` is the per-project key the sessions belong to (used
// only as a stable data attribute / for the caller's bookkeeping). Sessions are
// rendered NEWEST FIRST.
export function renderSessionHistory(projectKey: string, sessions: Session[]): HTMLElement {
  const root = document.createElement('section');
  root.className = 'session-history';
  root.setAttribute('data-component', 'session-history');
  root.dataset.projectKey = projectKey;

  const head = document.createElement('div');
  head.className = 'session-history__head';
  head.innerHTML = `<span class="session-history__title">historique des sessions</span>`;
  root.appendChild(head);

  if (sessions.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'session-history__empty';
    empty.textContent = 'aucune session enregistrée';
    root.appendChild(empty);
    return root;
  }

  const list = document.createElement('div');
  list.className = 'session-history__list';
  // Newest first — sort by startedAt descending (stable for equal/empty values).
  const ordered = [...sessions].sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  for (const session of ordered) list.appendChild(renderSessionRow(session));
  root.appendChild(list);

  return root;
}
